import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingBag, CreditCard, MapPin, Settings, LogOut, Edit2, Loader, AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import apiCache from '../../utils/apiCache';
import './index.css';
// Import the section modals
import { MyOrdersModal } from '../MyOrdersModal';
import { MyTransactionsModal } from '../MyTransactionsModal';
import { ManageAddressesModal } from '../ManageAddressesModal';
import { PreferencesModal } from '../PreferencesModal';
import ChangePasswordModal from '../ChangePasswordModal';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const UserProfileModal = ({ isOpen, onClose, userPhone, userId, isLoggedIn, onLoginClick, onLogout, onProfileUpdated, authMethod }) => {
  const [activeModal, setActiveModal] = useState(null);

  // Profile data from API
  const [profileData, setProfileData] = useState({
    id: null,
    name: '',
    email: null,
    phone: '',
    country_code: '+91',
    image: null,
    created_at: null,
    loyalty: null
  });

  // Edit form data
  const [editData, setEditData] = useState({ name: '', phone: '', email: '' });

  // Loading & error states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [hasFetched, setHasFetched] = useState(false);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = getValidAuthSession()?.token;
    if (!token) return null;
    return {
      'Authorization': `Bearer ${token}`,
      'X-Api-Key': X_API_KEY,
      'X-Tenant': getTenantId(),
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }, []);

  // Fetch profile from API
  const fetchProfile = useCallback(async () => {
    // 1. CHECK CACHE FIRST
    const cachedProfile = apiCache.getUserProfile();
    if (cachedProfile) {
      console.log('⚡ UserProfileModal using cached profile data');
      setProfileData({
        id: cachedProfile.id,
        name: cachedProfile.name || '',
        email: cachedProfile.email || null,
        phone: cachedProfile.phone || '',
        country_code: cachedProfile.country_code || '+91',
        image: cachedProfile.image || null,
        created_at: cachedProfile.created_at || null,
        loyalty: cachedProfile.loyalty || null
      });
      setEditData({
        name: cachedProfile.name || '',
        phone: cachedProfile.phone || '',
        email: cachedProfile.email || ''
      });
      setHasFetched(true);
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) {
      setError('Please login to view your profile');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/web/me`, { headers });

      if (response.data && response.data.data) {
        const data = response.data.data;
        // 2. STORE IN CACHE
        apiCache.setUserProfile(data);

        setProfileData({
          id: data.id,
          name: data.name || '',
          email: data.email || null,
          phone: data.phone || '',
          country_code: data.country_code || '+91',
          image: data.image || null,
          created_at: data.created_at || null,
          loyalty: data.loyalty || null
        });
        setEditData({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || ''
        });
        setHasFetched(true);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      const message = err.response?.data?.message
        || err.response?.data?.errors?.toString()
        || 'Failed to load profile. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch profile when modal opens and user is logged in
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      // Reset non-cached local states when modal opens
      setSaveError(null);
      setToast(null);
      setImageFile(null);
      setImagePreview(null);

      // Fetch or use cache
      fetchProfile();
    }
  }, [isOpen, isLoggedIn, fetchProfile]);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  if (!isOpen) return null;

  const handleLoginClick = () => {
    onClose();
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const handleLogoutClick = () => {
    onClose();
    if (onLogout) {
      onLogout();
    }
  };

  const handleCancelEdit = () => {
    onClose();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSaveError('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setSaveError(null);
    }
  };

  const handleSaveChanges = async () => {
    // Validation
    const trimmedName = editData.name.trim();
    const trimmedPhone = editData.phone.trim();

    if (!trimmedName) {
      setSaveError('Name is required.');
      return;
    }

    if (!trimmedPhone) {
      setSaveError('Phone number is required.');
      return;
    }

    // Basic phone validation (digits only, reasonable length)
    const phoneDigits = trimmedPhone.replace(/[^0-9]/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      setSaveError('Please enter a valid phone number (7-15 digits).');
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) {
      setSaveError('Session expired. Please login again.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      let payload = new FormData();
      payload.append('name', trimmedName);
      payload.append('phone', trimmedPhone);
      payload.append('email', editData.email.trim());

      let config = {
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        }
      };
      let endpoint = `${API_BASE_URL}/web/me`;

      if (imageFile) {
        payload.append('image', imageFile);
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        // If no image, we can send as JSON if preferred, 
        // but since we're consolidating, using FormData for both or switching back to JSON is fine.
        // Let's stick to the user's request: "Same as we send name, phone"
        payload = {
          name: trimmedName,
          phone: trimmedPhone,
          email: editData.email.trim()
        };
      }

      const response = await axios.put(endpoint, payload, config);

      if (response.data && response.data.data) {
        const data = response.data.data;
        // 3. UPDATE CACHE WITH NEW DATA
        apiCache.setUserProfile(data);
        window.dispatchEvent(new Event('profile_updated'));

        setProfileData((prev) => ({
          id: data.id,
          name: data.name || '',
          email: data.email || null,
          phone: data.phone || '',
          country_code: data.country_code || '+91',
          image: data.image || null,
          created_at: data.created_at || null,
          loyalty: data.loyalty || prev.loyalty
        }));
        setToast({ type: 'success', message: 'Profile updated successfully!' });
        if (onProfileUpdated) {
          onProfileUpdated(data);
        }
        setTimeout(onClose, 1500);
      }
    } catch (err) {
      console.error('Error updating profile:', err);

      // Handle validation errors from API
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat().join('. ');
        setSaveError(errorMessages || 'Validation failed. Please check your input.');
      } else {
        const message = err.response?.data?.message || 'Failed to update profile. Please try again.';
        setSaveError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    // Clear save error when user starts typing
    if (saveError) setSaveError(null);
  };

  const handleSectionClick = (section) => {
    setActiveModal(section);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  // Check if profile image is the default placeholder
  const isDefaultImage = (imageUrl) => {
    if (!imageUrl) return true;
    return imageUrl.includes('default/profile');
  };

  return (
    <>
      <div className="user-profile-overlay" onClick={onClose}>
        <div className="user-profile-modal user-profile-dropdown" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="user-profile-close">
            <X size={24} />
          </button>

          {/* Toast Notification */}
          {toast && (
            <div className={`user-profile-toast user-profile-toast-${toast.type}`}>
              {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <span>{toast.message}</span>
            </div>
          )}

          <div className="user-profile-content">
            {isLoggedIn ? (
              <>
                {loading ? (
                  <div className="user-profile-loading">
                    <Loader size={36} className="user-profile-spinner" />
                    <p>Loading profile...</p>
                  </div>
                ) : error ? (
                  <div className="user-profile-error">
                    <AlertCircle size={40} />
                    <p>{error}</p>
                    <button className="user-profile-retry-button" onClick={fetchProfile}>
                      Try Again
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="user-profile-title">Edit Profile</h2>

                    <div className="user-profile-photo-upload-container">
                      <div className="user-profile-photo-upload">
                        <div className="user-profile-photo-display-wrapper">
                          {!(imagePreview ? false : isDefaultImage(profileData.image)) ? (
                            <img src={imagePreview || profileData.image} alt="Preview" className="user-profile-photo-preview" />
                          ) : (
                            <div className="user-profile-photo-placeholder">
                              <i className="fa fa-user-circle" aria-hidden="true"></i>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="user-profile-photo-input"
                            title="Click to change photo"
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>

                    {profileData.loyalty && (
                      <div className="user-profile-loyalty-summary">
                        <div className="user-profile-loyalty-top">
                          <div>
                            <div className="user-profile-loyalty-points-label">Loyalty Points</div>
                            <div className="user-profile-loyalty-points-value">
                              {profileData.loyalty.points?.available ?? 0} pts
                              {profileData.loyalty.points?.lifetime != null && (
                                <span className="user-profile-loyalty-points-label"> &nbsp;·&nbsp; {profileData.loyalty.points.lifetime} lifetime</span>
                              )}
                            </div>
                          </div>
                          <div className="user-profile-loyalty-tier">
                            {profileData.loyalty.tier?.display ?? 'Bronze'}
                          </div>
                        </div>
                        {typeof profileData.loyalty.tier?.progress?.progress_percentage === 'number' && (
                          <>
                            <div className="user-profile-loyalty-progress-track">
                              <div
                                className="user-profile-loyalty-progress-fill"
                                style={{ width: `${Math.min(100, Math.max(0, profileData.loyalty.tier.progress.progress_percentage))}%` }}
                              />
                            </div>
                            {profileData.loyalty.tier.progress.next_tier && (
                              <div className="user-profile-loyalty-progress-note">
                                {profileData.loyalty.tier.progress.progress_percentage}% to {profileData.loyalty.tier.progress.next_tier}
                                {Number(profileData.loyalty.tier.progress.points_to_next_tier) > 0 && (
                                  ` · ${profileData.loyalty.tier.progress.points_to_next_tier} pts to go`
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <div className="user-profile-edit-form">
                      <div className="user-profile-form-field">
                        <label className="user-profile-form-label">Name</label>
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="user-profile-form-input"
                          placeholder="Enter your name"
                          disabled={saving}
                          maxLength={100}
                        />
                      </div>

                      <div className="user-profile-form-field">
                        <label className="user-profile-form-label">Phone Number</label>
                        <div className="user-profile-phone-input-wrapper">
                          <span className="user-profile-phone-prefix">{profileData.country_code}</span>
                          <input
                            type="tel"
                            value={editData.phone}
                            onChange={(e) => {
                              // Allow only digits
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              handleInputChange('phone', val);
                            }}
                            className="user-profile-form-input user-profile-phone-input"
                            placeholder="Enter phone number"
                            disabled={saving}
                            maxLength={15}
                          />
                        </div>
                      </div>

                      <div className="user-profile-form-field">
                        <label className="user-profile-form-label">Email Address</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="user-profile-form-input"
                          placeholder="Enter your email"
                          disabled={saving}
                        />
                      </div>

                      {/* Save Error Message */}
                      {saveError && (
                        <div className="user-profile-save-error">
                          <AlertCircle size={14} />
                          <span>{saveError}</span>
                        </div>
                      )}
                    </div>

                    {authMethod === 'email_password' && (
                      <div className="user-profile-change-pw-trigger">
                        <button
                          className="user-profile-change-pw-btn"
                          onClick={() => handleSectionClick('change-password')}
                          type="button"
                        >
                          <Lock size={16} />
                          <span>Change Password</span>
                        </button>
                        <p className="user-profile-change-pw-note">Click here to update your account password security.</p>
                      </div>
                    )}

                    <div className="user-profile-button-group">
                      <button
                        className="user-profile-cancel-button"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        <i className="fa fa-times" aria-hidden="true"></i>
                        <span>Cancel</span>
                      </button>
                      <button
                        className="user-profile-save-button"
                        onClick={handleSaveChanges}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader size={14} className="user-profile-spinner" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <i className="fa fa-check" aria-hidden="true"></i>
                            <span>Save Changes</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="user-profile-icon">
                  <i className="fa fa-user-circle" aria-hidden="true"></i>
                </div>
                <h2 className="user-profile-title">Not Logged In</h2>

                <div className="user-profile-empty">
                  <p>You are not currently logged in.</p>
                  <p>Add an item to your cart to log in and place an order.</p>
                </div>

                <button className="user-profile-login-button" onClick={handleLoginClick}>
                  <i className="fa fa-sign-in" aria-hidden="true"></i>
                  <span>Login Now</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Section Modals */}
      <SectionModals
        activeModal={activeModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

// Section Modals Component (includes all 4 section modals)
const SectionModals = ({ activeModal, onClose }) => {
  return (
    <>
      <MyOrdersModal isOpen={activeModal === 'orders'} onClose={onClose} />
      <MyTransactionsModal isOpen={activeModal === 'transactions'} onClose={onClose} />
      <ManageAddressesModal isOpen={activeModal === 'addresses'} onClose={onClose} />
      <PreferencesModal isOpen={activeModal === 'preferences'} onClose={onClose} />
      <ChangePasswordModal isOpen={activeModal === 'change-password' || activeModal === true} onClose={onClose} />
    </>
  );
};


export default UserProfileModal;